import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Link as MuiLink, 
  Divider, 
  IconButton,
  styled
} from '@mui/material';
import { 
  Facebook as FacebookIcon, 
  Instagram as InstagramIcon, 
  LinkedIn as LinkedinIcon, 
  Twitter as TwitterIcon 
} from '@mui/icons-material';
import InstationLogo from '../Instation-complete.svg';

// Styled components
const FooterContainer = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  padding: 0,
  width: '100%'
}));

const FooterLink = styled(MuiLink)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.text.primary,
  }
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2)
}));

const FooterText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.6
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent'
  }
}));

const Logo = styled('img')({
  height: 45,
  width: 'auto'
});

export function Footer() {
  return (
    <FooterContainer>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3}>
          {/* Logo and description */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <MuiLink href="/" underline="none">
                <Logo src={InstationLogo} alt="Instation" />
              </MuiLink>
              <FooterText>
                Boost your LinkedIn presence with AI-powered content creation.
                Generate viral posts, grow your network, and increase engagement -
                all while saving hours of your time every week.
              </FooterText>
            </Box>
          </Grid>

          {/* Links section */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Product links */}
              <Grid item xs={12} sm={4}>
                <FooterHeading>Product</FooterHeading>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FooterLink href="/features">Features</FooterLink>
                  <FooterLink href="/pricing">Pricing</FooterLink>
                  <FooterLink href="/free-tools">Free Tools</FooterLink>
                  <FooterLink href="/faq">FAQ</FooterLink>
                </Box>
              </Grid>

              {/* Company links */}
              <Grid item xs={12} sm={4}>
                <FooterHeading>Company</FooterHeading>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FooterLink href="/about-us">About</FooterLink>
                  <FooterLink href="#">Blog</FooterLink>
                  <FooterLink href="#">Careers</FooterLink>
                  <FooterLink href="/contact">Contact</FooterLink>
                </Box>
              </Grid>

              {/* Legal links */}
              <Grid item xs={12} sm={4}>
                <FooterHeading>Legal</FooterHeading>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
                  <FooterLink href="/terms-and-conditions">Terms & Conditions</FooterLink>
                  <FooterLink href="/cookie-policy">Cookie Policy</FooterLink>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1
          }}>
            <FooterText>
              © 2025 instation. All rights reserved.
            </FooterText>
            <FooterText sx={{ fontSize: '0.75rem', maxWidth: 600, opacity: 0.8 }}>
              Disclaimer: Instation is not endorsed by or affiliated with LinkedIn Corporation, which is registered in
              the U.S. and other countries. LinkedIn is a trademark of LinkedIn Corporation.
            </FooterText>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <SocialIcon aria-label="Twitter">
              <TwitterIcon fontSize="small" />
            </SocialIcon>
            <SocialIcon aria-label="LinkedIn">
              <LinkedinIcon fontSize="small" />
            </SocialIcon>
            <SocialIcon aria-label="Facebook">
              <FacebookIcon fontSize="small" />
            </SocialIcon>
            <SocialIcon aria-label="Instagram">
              <InstagramIcon fontSize="small" />
            </SocialIcon>
          </Box>
        </Box>
      </Container>
    </FooterContainer>
  );
}